import React from 'react'
import { View, Text, StyleSheet } from 'react-native'
import PartiText from '@partipost-cm-new/app-preview/dist/Components/PartiText'
import { color } from '@partipost-cm-new/app-preview/dist/Styles/Base'
import { ValueObject } from 'state/appPreview'

export interface PlaceholderComponentProps {
    template: string
    values?: ValueObject[]
}

export const PlaceholderComponent: React.FC<PlaceholderComponentProps> = ({ template, values }) => {
    const generateUrlComponent = (value: string, concatSymbol: string) => {
        const charactersArray = value.split(concatSymbol)
        return charactersArray.flatMap((url, index) => [
            <PartiText key={`url-text-${index}`} style={styles.linkStyle}>
                {url}
            </PartiText>,
            index < charactersArray.length - 1 ? (
                <PartiText key={`concat-symbol-${index}`}>{concatSymbol}</PartiText>
            ) : null,
        ])
    }

    const generateUrlListComponent = (value: string, concatSymbol = '\u002D') => {
        const charactersArray = value.split('\n')
        return charactersArray.flatMap((url, index) => [
            <View style={styles.inlineContainer} key={`url-text-list-${index}`}>
                <PartiText>
                    {concatSymbol}
                    <PartiText style={styles.linkStyle}>{url.replace(concatSymbol, '')}</PartiText>
                </PartiText>
            </View>,
            index < charactersArray.length - 1 ? <br /> : null,
        ])
    }

    const generateInlineComponent = (
        text: string,
        key: string,
        value: string,
        isPlaceholder: boolean,
        isUrl = false,
        concatSymbol = ' ',
    ) => {
        const pattern = '{{' + key + '}}'
        const textSplitArray = text.split(pattern)
        return (
            <Text style={styles.inlineContainer}>
                <PartiText>{textSplitArray[0]}</PartiText>
                {isUrl && !isPlaceholder ? (
                    generateUrlComponent(value, concatSymbol)
                ) : (
                    <PartiText style={isPlaceholder ? styles.emptyValue : {}}>{value}</PartiText>
                )}

                {textSplitArray.length > 1 && <PartiText>{textSplitArray[1]}</PartiText>}
            </Text>
        )
    }

    const renderTextWithPlaceholders = (text: string, valueObjects?: ValueObject[]) => {
        // Regex to match placeholders in the format {{key}}
        const placeholderRegex = /{{(\w+)}}/g
        let lastIndex = 0
        const segments = []
        let match

        // Iterate through all matches of the placeholder regex in the text
        while ((match = placeholderRegex.exec(text)) !== null) {
            // Extract the key from the placeholder (e.g., 'purchase_locations' from '{{purchase_locations}}')
            const key = match[1]
            // Find the corresponding value object for this key
            const valueObject = valueObjects?.find(obj => key in obj)
            // Add any text before the current placeholder to the result
            if (match.index > lastIndex) {
                segments.push(<PartiText key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</PartiText>)
            }

            if (valueObject) {
                const value = valueObject[key]
                const displayValue = value || valueObject.emptyValue

                if (value === undefined || value === '') {
                    // If the value is empty or undefined, use the emptyValue
                    if (valueObject.isInline) {
                        const textComponent = generateInlineComponent(match[0], key, valueObject.emptyValue, true)
                        segments.push(textComponent)
                    } else {
                        segments.push(
                            <View key={`value-container-${match.index}`} style={styles.nonInlineContainer}>
                                <PartiText
                                    key={`value-${match.index}`}
                                    style={{
                                        ...styles.nonInlineText,
                                        ...(!value ? styles.emptyValue : valueObject.isUrl ? styles.linkStyle : {}),
                                    }}
                                >
                                    {valueObject.emptyValue}
                                </PartiText>
                            </View>,
                        )
                    }
                } else {
                    // Otherwise if there's a value, use it
                    if (valueObject.isInline) {
                        const textComponent = generateInlineComponent(
                            match[0],
                            key,
                            displayValue.toString(),
                            false,
                            valueObject.isUrl,
                            valueObject.concatSymbol,
                        )
                        segments.push(textComponent)
                    } else {
                        if (valueObject.isUrl) {
                            const textComponent = generateUrlListComponent(
                                displayValue.toString(),
                                valueObject.concatSymbol,
                            )
                            segments.push(textComponent)
                        } else {
                            segments.push(
                                <View key={`value-container-${match.index}`} style={styles.nonInlineContainer}>
                                    <PartiText
                                        key={`value-${match.index}`}
                                        style={{
                                            ...styles.nonInlineText,
                                            ...(!value ? styles.emptyValue : valueObject.isUrl ? styles.linkStyle : {}),
                                        }}
                                    >
                                        {displayValue}
                                    </PartiText>
                                </View>,
                            )
                        }
                    }
                }
                // Update lastIndex to continue searching after this match
                lastIndex = match.index + match[0].length
            }
        }

        // Add any remaining text after the last placeholder if there is any
        if (lastIndex < text.length) {
            segments.push(<PartiText key={`text-${lastIndex}`}>{text.slice(lastIndex)}</PartiText>)
        }

        return (
            <View style={styles.container}>
                <Text style={styles.inlineContainer}>{segments}</Text>
            </View>
        )
    }

    // Render the component with the processed template
    return <View style={styles.container}>{renderTextWithPlaceholders(template, values)}</View>
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: 1,
        width: '100%',
    },
    inlineContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        flex: 1,
        width: '100%',
    },
    nonInlineContainer: {
        width: '100%',
        marginVertical: 4, // Add some spacing between non-inline items
    },
    nonInlineText: {
        width: '100%',
    },
    emptyValue: {
        color: color.silverChalice,
    },
    linkStyle: {
        color: '#008BFF',
        wordBreak: 'break-all',
    },
    textStyle: {
        width: '100%',
        flexShrink: 1,
    },
})

export default PlaceholderComponent
